import type { CompanyIntegration } from '@entities/integration';
import type { UserPhoneNumber } from '@entities/user';
import type { CompanyIntegrationsClient } from '@usecases/store-integration-settings.usecase';

import { BusIframe } from '@main/bus-layer/bus-iframe';
import type { BusEvent } from '@main/utils/bus-event-promisifier';
import { BusEventsPromisifier } from '@main/utils/bus-event-promisifier';
import type {
  BusIntegrationsPayload,
  BusRawIntegration,
} from '@main/event-listeners/payloads/bus-integrations.payload';
import { Logger } from '@main/utils/logger';

/**
 * Bus-backed fetch of company integrations.
 *
 * Sends API.GET_INTEGRATIONS to the Bus (no payload — companyId is derived
 * server-side from the JWT) and receives the raw GraphQL getIntegrations array.
 * The Bus does NO mapping/filtering, so this publisher maps each raw
 * integration to a CompanyIntegration and applies three filters: SCV app
 * allowlist, non-inactive status, and phone-number ownership.
 */
export class FetchCompanyIntegrationsPublisher
  implements CompanyIntegrationsClient
{
  private logger = new Logger();

  async fetchCompanyIntegrations(
    phoneNumbers: UserPhoneNumber[],
  ): Promise<CompanyIntegration[]> {
    const iframe = await BusIframe.getIframe();

    const event: BusEvent = {
      type: 'API.GET_INTEGRATIONS',
    };

    this.logger.info('Publishing bus event', { action: event.type });

    const response =
      await BusEventsPromisifier.promisifySyncEvent<BusIntegrationsPayload>({
        event,
        fulfilled: 'API.GET_INTEGRATIONS_FULFILLED',
        rejected: 'API.GET_INTEGRATIONS_REJECTED',
        target: iframe,
      });

    const rawIntegrations = response ?? [];

    return this.filterIntegrations(rawIntegrations, phoneNumbers);
  }

  /**
   * Maps raw Bus integrations to CompanyIntegration and returns only the
   * salesforce SCV active integrations that belong to the current logged-in
   * user.
   */
  private filterIntegrations(
    rawIntegrations: BusRawIntegration[],
    phoneNumbers: UserPhoneNumber[],
  ): CompanyIntegration[] {
    const salesforceScvAppIds: number[] =
      process.env.SALESFORCE_SCV_APP_ID.split(',').map((id) =>
        parseInt(id.trim()),
      );

    const phoneNumberIds = phoneNumbers.map((num) => num.id);

    return rawIntegrations
      .map(
        ({
          id,
          active,
          applicationId,
          numberIds,
        }: BusRawIntegration): CompanyIntegration => ({
          id: Number(id),
          applicationId: Number(applicationId),
          status: active ? 'active' : 'inactive',
          numberIds: numberIds ?? [],
        }),
      )
      .filter((integration) => {
        return (
          salesforceScvAppIds.includes(integration.applicationId) &&
          integration.status !== 'inactive' &&
          integration.numberIds.reduce((prev, curr) => {
            return prev || phoneNumberIds.includes(curr);
          }, false)
        );
      });
  }
}
